import apiClient from './api'

const WINDOW_SECONDS_OPTIONS = [60, 3600, 86400, 2592000]

function cleanParams(params = {}) {
  const result = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === 'string' && value.trim() === '') return
    result[key] = value
  })
  return result
}

function asString(value, fallback = '') {
  if (value == null) return fallback
  return String(value)
}

function asNumber(value, fallback = null) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizePolicy(row = {}) {
  return {
    id: asString(row.id),
    subjectType: asString(row.subjectType),
    subjectId: row.subjectId == null ? null : asString(row.subjectId),
    endpointGroup: asString(row.endpointGroup),
    windowSeconds: asNumber(row.windowSeconds, 3600),
    maxRequests: asNumber(row.maxRequests, 0),
    maxCostUnits: row.maxCostUnits == null ? null : asNumber(row.maxCostUnits, null),
    maxConcurrentJobs: row.maxConcurrentJobs == null ? null : asNumber(row.maxConcurrentJobs, null),
    dailyMaxRequests: row.dailyMaxRequests == null ? null : asNumber(row.dailyMaxRequests, null),
    monthlyMaxRequests: row.monthlyMaxRequests == null ? null : asNumber(row.monthlyMaxRequests, null),
    status: asString(row.status, 'active'),
    priority: asNumber(row.priority, 100),
    reason: row.reason == null ? null : asString(row.reason),
    createdByUserId: row.createdByUserId == null ? null : asString(row.createdByUserId),
    updatedByUserId: row.updatedByUserId == null ? null : asString(row.updatedByUserId),
    effectiveFrom: row.effectiveFrom || null,
    effectiveTo: row.effectiveTo || null,
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  }
}

function normalizeUsageBucket(row = {}) {
  return {
    id: asString(row.id),
    subjectType: asString(row.subjectType),
    subjectId: row.subjectId == null ? null : asString(row.subjectId),
    endpointGroup: asString(row.endpointGroup),
    bucketType: asString(row.bucketType),
    bucketStart: row.bucketStart || null,
    requestCount: asNumber(row.requestCount, 0),
    acceptedCount: asNumber(row.acceptedCount, 0),
    rejectedCount: asNumber(row.rejectedCount, 0),
    costUnits: asNumber(row.costUnits, 0),
    lastRequestAt: row.lastRequestAt || null,
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  }
}

function normalizeUsageSummary(summary = {}) {
  return {
    requestCount: asNumber(summary.requestCount, 0),
    acceptedCount: asNumber(summary.acceptedCount, 0),
    rejectedCount: asNumber(summary.rejectedCount, 0),
    costUnits: asNumber(summary.costUnits, 0),
  }
}

function normalizeListResponse(response) {
  const rows = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data?.items) ? response.data.items : []
  return rows.map(normalizePolicy)
}

function normalizeUsageResponse(response) {
  const items = Array.isArray(response?.data?.data?.items)
    ? response.data.data.items
    : Array.isArray(response?.data?.items)
      ? response.data.items
      : []
  return {
    items: items.map(normalizeUsageBucket),
    summary: normalizeUsageSummary(response?.data?.data?.summary || response?.data?.summary),
  }
}

function normalizeError(error, fallbackMessage) {
  return {
    status: Number(error?.response?.status || 0),
    message: error?.response?.data?.message || error?.message || fallbackMessage,
    code: error?.response?.data?.error || error?.response?.data?.code || null,
  }
}

export async function listPublicApiLimitPolicies(params = {}) {
  try {
    const response = await apiClient.get('/admin/public-api/limit-policies', { params: cleanParams(params) })
    return normalizeListResponse(response)
  } catch (error) {
    throw normalizeError(error, 'Unable to load public API limit policies')
  }
}

export async function getPublicApiLimitPolicy(id) {
  const policyId = asString(id).trim()
  if (!policyId) throw { status: 400, message: 'Policy id is required' }

  try {
    const response = await apiClient.get(`/admin/public-api/limit-policies/${policyId}`)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to load public API limit policy')
  }
}

export async function createPublicApiLimitPolicy(payload) {
  try {
    const response = await apiClient.post('/admin/public-api/limit-policies', payload)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to create public API limit policy')
  }
}

export async function updatePublicApiLimitPolicy(id, payload) {
  const policyId = asString(id).trim()
  if (!policyId) throw { status: 400, message: 'Policy id is required' }

  try {
    const response = await apiClient.patch(`/admin/public-api/limit-policies/${policyId}`, payload)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to update public API limit policy')
  }
}

export async function disablePublicApiLimitPolicy(id) {
  const policyId = asString(id).trim()
  if (!policyId) throw { status: 400, message: 'Policy id is required' }

  try {
    const response = await apiClient.delete(`/admin/public-api/limit-policies/${policyId}`)
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to disable public API limit policy')
  }
}

export async function listPublicApiUsage(params = {}) {
  try {
    const response = await apiClient.get('/admin/public-api/usage', { params: cleanParams(params) })
    return normalizeUsageResponse(response)
  } catch (error) {
    throw normalizeError(error, 'Unable to load public API usage')
  }
}

export async function deletePublicApiUsageBucket(id) {
  const bucketId = asString(id).trim()
  if (!bucketId) throw { status: 400, message: 'Usage bucket id is required' }

  try {
    const response = await apiClient.delete(`/admin/public-api/usage/${bucketId}`)
    return normalizeUsageBucket(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to delete public API usage bucket')
  }
}

export async function clearPublicApiUsage() {
  try {
    const response = await apiClient.delete('/admin/public-api/usage')
    return response?.data?.data || response?.data || { count: 0 }
  } catch (error) {
    throw normalizeError(error, 'Unable to clear public API usage')
  }
}

export async function getPublicApiEffectivePolicy(params) {
  try {
    const response = await apiClient.get('/admin/public-api/effective-policy', { params: cleanParams(params) })
    return normalizePolicy(response?.data?.data || response?.data)
  } catch (error) {
    throw normalizeError(error, 'Unable to load effective public API policy')
  }
}

export { WINDOW_SECONDS_OPTIONS, normalizePolicy, normalizeUsageBucket }
